import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { QuestionRenderer, QuestionAnswerValue } from '@/components/assessment-engine/QuestionRenderer';
import { DemographicQuestionsGrid } from '@/components/assessment-engine/DemographicQuestionsGrid';
import { isDemographicsQuestionList } from '@/components/assessment-engine/demographicFieldLayout';
import { flattenVisibleQuestions } from '@/components/assessment-engine/flattenSections';
import { TakeAssessmentCampaignTitle } from '@/components/assessment-engine/TakeAssessmentCampaignTitle';
import { getNextQuestionKey, orderQuestionsByNavigation, nextPresentationStepIndex } from '@/components/assessment-engine/questionNavigation';
import {
  buildPresentationSteps,
  type PresentationStep,
} from '@/components/assessment-engine/presentationSteps';
import {
  isRequiredQuestionAnswered,
  unansweredRequiredQuestions,
} from '@/components/assessment-engine/answerValidation';
import { answerValueToMapEntry } from '@/components/assessment-engine/answerMapEntry';
import {
  AE_ASSESSMENT_CONTENT_WIDTH,
  AE_BTN_PRIMARY,
  AE_BTN_SECONDARY,
  AE_CARD,
  AE_CARD_COMPACT,
  AE_MUTED,
  AE_SECTION_TITLE,
  AE_SPINNER,
  AE_TITLE,
} from '@/components/assessment-engine/layout/aeTheme';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import type {
  AeBranding,
  AeEvaluatedQuestion,
  AePresentationStyle,
  AePreviewSessionResult,
  AeRespondentFraming,
} from '@/types/assessmentEngine';

export type AssessmentPreviewTarget =
  | { kind: 'template'; templateId: string }
  | { kind: 'campaign'; campaignId: string };

type PreviewStep = 'loading' | 'questions' | 'complete' | 'error';

export interface AssessmentPreviewFlowProps {
  target: AssessmentPreviewTarget;
  onClose: () => void;
  skipRequired: boolean;
  onSkipRequiredChange: (value: boolean) => void;
}

function parseBranding(raw: AePreviewSessionResult['branding']): AeBranding {
  if (!raw || typeof raw !== 'object') return {};
  return raw as AeBranding;
}

function hasAnswerMapValues(map: Record<string, string | number | boolean | null>): boolean {
  return Object.values(map).some(
    (v) => v !== null && v !== undefined && String(v).trim() !== ''
  );
}

function hasAnswerContent(ans: QuestionAnswerValue): boolean {
  if (ans.is_na) return true;
  if (ans.answer_numeric != null && !Number.isNaN(ans.answer_numeric)) return true;
  if (typeof ans.answer_text === 'string' && ans.answer_text.trim().length > 0) return true;
  if (Array.isArray(ans.answer_values) && ans.answer_values.length > 0) return true;
  if (ans.answer_matrix && Object.keys(ans.answer_matrix).length > 0) return true;
  return false;
}

/** Respondent take-flow UI for preview (no public header or admin chrome). */
export const AssessmentPreviewFlow: React.FC<AssessmentPreviewFlowProps> = ({
  target,
  onClose,
  skipRequired,
  onSkipRequiredChange,
}) => {
  const { t } = useLanguage();
  const isTemplatePreview = target.kind === 'template';
  const entityId = isTemplatePreview ? target.templateId : target.campaignId;

  const [step, setStep] = useState<PreviewStep>('loading');
  const [preview, setPreview] = useState<AePreviewSessionResult | null>(null);
  const [questions, setQuestions] = useState<AeEvaluatedQuestion[]>([]);
  const [answerMap, setAnswerMap] = useState<Record<string, string | number | boolean | null>>({});
  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState<QuestionAnswerValue>({});
  const [presentationStyle, setPresentationStyle] = useState<AePresentationStyle>('question');
  const [presentationSteps, setPresentationSteps] = useState<PresentationStep[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepAnswers, setStepAnswers] = useState<Record<string, QuestionAnswerValue>>({});
  const [busy, setBusy] = useState(false);

  const branding = parseBranding(preview?.branding);
  const framing: AeRespondentFraming | null | undefined = preview?.respondent_framing;
  const current = questions[index];
  const currentPresentationStep = presentationSteps[stepIndex];
  const isPagedMode = presentationStyle !== 'question';

  const loadPreview = useCallback(
    async (answers: Record<string, string | number | boolean | null>) => {
      if (isTemplatePreview) {
        return assessmentEngineService.previewTemplate(entityId, answers);
      }
      return assessmentEngineService.previewCampaign(entityId, answers);
    },
    [entityId, isTemplatePreview]
  );

  const applyPreviewSession = useCallback((session: AePreviewSessionResult) => {
    const style = session.presentation_style ?? 'question';
    setPreview(session);
    setPresentationStyle(style);

    const flat = flattenVisibleQuestions(session.sections);
    const ordered = orderQuestionsByNavigation(flat, session.sections, session.logic_rules ?? [], {});
    setQuestions(ordered);

    if (style !== 'question') {
      setPresentationSteps(buildPresentationSteps(style, session.sections));
      setStepIndex(0);
    } else {
      setIndex(0);
    }

    setStep('questions');
  }, []);

  useEffect(() => {
    setStep('loading');
    loadPreview({})
      .then(applyPreviewSession)
      .catch(() => setStep('error'));
  }, [entityId, loadPreview, applyPreviewSession]);

  const reevaluate = async (nextAnswers: Record<string, string | number | boolean | null>) => {
    const session = await loadPreview(nextAnswers);
    setPreview(session);
    return session;
  };

  const saveAndAdvance = useCallback(async () => {
    if (!current) return;
    if (!skipRequired && !isRequiredQuestionAnswered(current, answer)) {
      toast.error(t('assessmentEngine.take.requiredAnswer'));
      return;
    }

    if (skipRequired) {
      const nextAnswers = hasAnswerContent(answer)
        ? { ...answerMap, [current.question_key]: answerValueToMapEntry(answer) }
        : answerMap;
      setAnswerMap(nextAnswers);
      try {
        const session = await reevaluate(nextAnswers);
        const flat = flattenVisibleQuestions(session.sections);
        const ordered = orderQuestionsByNavigation(
          flat,
          session.sections,
          session.logic_rules ?? [],
          nextAnswers
        );
        setQuestions(ordered);
        const nextKey = getNextQuestionKey(
          current.question_key,
          session.sections,
          session.logic_rules ?? [],
          nextAnswers
        );
        if (!nextKey) {
          setStep('complete');
        } else {
          const nextIdx = ordered.findIndex((q) => q.question_key === nextKey);
          setIndex(nextIdx >= 0 ? nextIdx : index + 1);
          setAnswer({});
        }
      } catch {
        if (index + 1 >= questions.length) {
          setStep('complete');
        } else {
          setIndex((i) => i + 1);
          setAnswer({});
        }
      }
      return;
    }

    setBusy(true);
    try {
      const answerKey = current.question_key;
      const nextAnswers: Record<string, string | number | boolean | null> = {
        ...answerMap,
        [answerKey]: answerValueToMapEntry(answer),
      };
      setAnswerMap(nextAnswers);

      const session = await reevaluate(nextAnswers);
      const nextKey = getNextQuestionKey(
        answerKey,
        session.sections,
        session.logic_rules ?? [],
        nextAnswers
      );

      if (!nextKey) {
        setStep('complete');
      } else {
        const flat = flattenVisibleQuestions(session.sections);
        const ordered = orderQuestionsByNavigation(
          flat,
          session.sections,
          session.logic_rules ?? [],
          nextAnswers
        );
        const nextIdx = ordered.findIndex((q) => q.question_key === nextKey);
        setQuestions(ordered);
        setIndex(nextIdx >= 0 ? nextIdx : 0);
        setAnswer({});
      }
    } catch {
      toast.error(t('assessmentEngine.preview.failedLoad'));
    } finally {
      setBusy(false);
    }
  }, [current, answer, answerMap, questions.length, index, skipRequired, t]);

  const saveStepAndAdvance = useCallback(async () => {
    if (!currentPresentationStep) return;
    if (!skipRequired) {
      const missing = unansweredRequiredQuestions(currentPresentationStep.questions, stepAnswers);
      if (missing.length > 0) {
        toast.error(t('assessmentEngine.take.requiredAnswer'));
        return;
      }
    }

    if (skipRequired) {
      const nextAnswers = { ...answerMap };
      for (const q of currentPresentationStep.questions) {
        const ans = stepAnswers[q.id] ?? {};
        if (hasAnswerContent(ans)) {
          nextAnswers[q.question_key] = answerValueToMapEntry(ans);
        }
      }
      setAnswerMap(nextAnswers);
      try {
        const session = await reevaluate(
          hasAnswerMapValues(nextAnswers) ? nextAnswers : {}
        );
        const style = presentationStyle === 'subsection' ? 'subsection' : 'section';
        const rebuilt = buildPresentationSteps(style, session.sections);
        setPresentationSteps(rebuilt);

        const lastQ = currentPresentationStep.questions[currentPresentationStep.questions.length - 1];
        const nextIdx = lastQ
          ? nextPresentationStepIndex(
              currentPresentationStep,
              stepIndex,
              rebuilt,
              session.sections,
              session.logic_rules ?? [],
              nextAnswers,
              style
            )
          : stepIndex + 1;

        if (nextIdx >= rebuilt.length) {
          setStep('complete');
        } else {
          setStepIndex(nextIdx);
          setStepAnswers({});
        }
      } catch {
        const next = stepIndex + 1;
        if (next >= presentationSteps.length) {
          setStep('complete');
        } else {
          setStepIndex(next);
          setStepAnswers({});
        }
      }
      return;
    }

    setBusy(true);
    try {
      const nextAnswers = { ...answerMap };
      for (const q of currentPresentationStep.questions) {
        const ans = stepAnswers[q.id] ?? {};
        nextAnswers[q.question_key] = answerValueToMapEntry(ans);
      }
      setAnswerMap(nextAnswers);

      const session = await reevaluate(nextAnswers);
      const style = presentationStyle === 'subsection' ? 'subsection' : 'section';
      const rebuilt = buildPresentationSteps(style, session.sections);
      setPresentationSteps(rebuilt);

      const lastQ = currentPresentationStep.questions[currentPresentationStep.questions.length - 1];
      const nextIdx = lastQ
        ? nextPresentationStepIndex(
            currentPresentationStep,
            stepIndex,
            rebuilt,
            session.sections,
            session.logic_rules ?? [],
            nextAnswers,
            style
          )
        : stepIndex + 1;

      if (nextIdx >= rebuilt.length) {
        setStep('complete');
      } else {
        setStepIndex(nextIdx);
        setStepAnswers({});
      }
    } catch {
      toast.error(t('assessmentEngine.preview.failedLoad'));
    } finally {
      setBusy(false);
    }
  }, [
    currentPresentationStep,
    stepAnswers,
    answerMap,
    presentationStyle,
    stepIndex,
    presentationSteps.length,
    skipRequired,
    t,
  ]);

  const progress = useMemo(() => {
    if (isPagedMode) {
      if (!presentationSteps.length) return 0;
      return Math.round(((stepIndex + 1) / presentationSteps.length) * 100);
    }
    if (!questions.length) return 0;
    return Math.round(((index + 1) / questions.length) * 100);
  }, [isPagedMode, presentationSteps.length, stepIndex, index, questions.length]);

  const previewBanner = (
    <div
      className="bg-[var(--color-warning)]/15 border-b border-[var(--color-warning)]/40 px-4 py-2 text-sm"
      role="status"
    >
      <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-x-4 gap-y-2">
        <span>{t('assessmentEngine.preview.banner')}</span>
        <label className="inline-flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            className="rounded border-[var(--color-border)]"
            checked={skipRequired}
            onChange={(e) => onSkipRequiredChange(e.target.checked)}
          />
          <span>{t('assessmentEngine.preview.skipRequired')}</span>
        </label>
      </div>
    </div>
  );

  const contentShell = (content: React.ReactNode, title?: React.ReactNode) => (
    <div className="flex min-h-0 flex-1 flex-col">
      {previewBanner}
      <div className="flex-1 overflow-y-auto">
        <div className={`${AE_ASSESSMENT_CONTENT_WIDTH} px-4 py-6`}>
          {title}
          {content}
        </div>
      </div>
    </div>
  );

  const campaignTitle = (size: 'lg' | 'md' = 'lg') => (
    <TakeAssessmentCampaignTitle campaignName={preview?.name} branding={branding} size={size} />
  );

  if (step === 'loading') {
    return contentShell(
      <div className="flex justify-center py-16">
        <div className={AE_SPINNER} />
      </div>
    );
  }

  if (step === 'error') {
    return contentShell(
      <div className={`${AE_CARD} space-y-4`}>
        <p>{t('assessmentEngine.preview.failedLoad')}</p>
        <button type="button" className={AE_BTN_SECONDARY} onClick={onClose}>
          {t('assessmentEngine.preview.close')}
        </button>
      </div>
    );
  }

  if (step === 'complete') {
    return contentShell(
      <div className={`${AE_CARD} space-y-4`}>
        <h1 className={`${AE_TITLE} mb-2 text-center`}>{t('assessmentEngine.preview.completeTitle')}</h1>
        <p>{t('assessmentEngine.preview.completeMessage')}</p>
        {framing?.closing?.message?.trim() && (
          <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-4 space-y-1">
            <h2 className="text-sm font-semibold">{t('assessmentEngine.take.whatsNext')}</h2>
            <p className="text-sm whitespace-pre-wrap">{framing.closing.message}</p>
          </div>
        )}
        <button type="button" className={AE_BTN_PRIMARY} onClick={onClose}>
          {t('assessmentEngine.preview.close')}
        </button>
      </div>,
      campaignTitle()
    );
  }

  if (isPagedMode && currentPresentationStep) {
    const totalSteps = presentationSteps.length;
    const isDemoStep = isDemographicsQuestionList(currentPresentationStep.questions);
    return contentShell(
      <div className={`${isDemoStep ? AE_CARD_COMPACT : AE_CARD} space-y-4`}>
        <div>
          <div className="h-2 rounded bg-[var(--color-border)]">
            <div className="h-2 rounded bg-[var(--color-accent-bg)]" style={{ width: `${progress}%` }} />
          </div>
          <p className="mt-1 text-xs text-[var(--color-secondary-text)]">
            {t('assessmentEngine.take.stepProgress', { current: stepIndex + 1, total: totalSteps })}
          </p>
        </div>
        <div>
          <h2 className={isDemoStep ? 'text-base font-semibold text-[var(--color-surface-text)]' : AE_SECTION_TITLE}>
            {currentPresentationStep.label}
          </h2>
          {currentPresentationStep.overview && (
            <p className={`${AE_MUTED} ${isDemoStep ? 'text-xs mt-0.5 mb-2' : 'mt-1 mb-4'}`}>
              {currentPresentationStep.overview}
            </p>
          )}
        </div>
        {isDemoStep ? (
          <DemographicQuestionsGrid
            questions={currentPresentationStep.questions}
            answersByQuestionId={stepAnswers}
            onChange={(questionId, val) =>
              setStepAnswers((prev) => ({ ...prev, [questionId]: val }))
            }
          />
        ) : (
          currentPresentationStep.questions.map((q) => (
            <QuestionRenderer
              key={q.id}
              question={q}
              value={stepAnswers[q.id] ?? {}}
              onChange={(val) => setStepAnswers((prev) => ({ ...prev, [q.id]: val }))}
            />
          ))
        )}
        <div className="flex justify-between pt-1">
          <button
            type="button"
            className={`${AE_BTN_SECONDARY} disabled:opacity-40`}
            disabled={stepIndex === 0 || busy}
            onClick={() => {
              setStepIndex((i) => Math.max(0, i - 1));
              setStepAnswers({});
            }}
          >
            {t('assessmentEngine.take.back')}
          </button>
          <button type="button" disabled={busy} className={AE_BTN_PRIMARY} onClick={() => void saveStepAndAdvance()}>
            {skipRequired
              ? stepIndex + 1 >= totalSteps
                ? t('assessmentEngine.take.submit')
                : t('assessmentEngine.take.next')
              : busy
                ? t('assessmentEngine.take.saving')
                : stepIndex + 1 >= totalSteps
                  ? t('assessmentEngine.take.submit')
                  : t('assessmentEngine.take.next')}
          </button>
        </div>
      </div>,
      campaignTitle('md')
    );
  }

  return contentShell(
    <div className={`${AE_CARD} space-y-6`}>
      <div>
        <div className="h-2 rounded bg-[var(--color-border)]">
          <div className="h-2 rounded bg-[var(--color-accent-bg)]" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-1 text-xs text-[var(--color-secondary-text)]">
          {t('assessmentEngine.take.questionProgress', {
            current: index + 1,
            total: questions.length,
          })}
        </p>
      </div>
      {current && <QuestionRenderer question={current} value={answer} onChange={setAnswer} />}
      <div className="flex justify-between">
        <button
          type="button"
          className={`${AE_BTN_SECONDARY} disabled:opacity-40`}
          disabled={index === 0 || busy}
          onClick={() => {
            setIndex((i) => Math.max(0, i - 1));
            setAnswer({});
          }}
        >
          {t('assessmentEngine.take.back')}
        </button>
        <button
          type="button"
          disabled={busy && !skipRequired}
          className={AE_BTN_PRIMARY}
          onClick={() => void saveAndAdvance()}
        >
          {skipRequired
            ? index + 1 >= questions.length
              ? t('assessmentEngine.take.submit')
              : t('assessmentEngine.take.next')
            : busy
              ? t('assessmentEngine.take.saving')
              : index + 1 >= questions.length
                ? t('assessmentEngine.take.submit')
                : t('assessmentEngine.take.next')}
        </button>
      </div>
    </div>,
    campaignTitle('md')
  );
};
